import React, { useState, useEffect } from 'react';
import { useDashboard } from './DashboardContext';
import {
  User,
  Globe,
  Award,
  CheckCircle,
  Loader2,
  CreditCard,
  Cake,
  Pencil,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { uploadApi } from '../../services/api';
import CheckoutModal from '../../components/CheckoutModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProfilePage = () => {
  const { updateUser } = useAuth();
  const {
    user,
    profileName,
    setProfileName,
    tradingViewUsername,
    setTradingViewUsername,
    isUploading,
    uploadProgress,
    memberships,
    upgradingMembershipId,
    fileInputRef,
    formatDate,
    handleProfilePictureUpload,
    handleSaveProfileName,
    handleSaveTradingViewUsername,
    handleOpenBillingPortal,
    checkoutModalMembership,
    openCheckoutModal,
    closeCheckoutModal,
    handleCheckoutWithCoupon
  } = useDashboard();

  // Cache buster for profile picture - changes when profile_picture URL changes
  const [imageKey, setImageKey] = useState(Date.now());
  
  // Update image key when profile picture changes to force re-render
  useEffect(() => {
    if (user?.profile_picture) {
      setImageKey(Date.now());
    }
  }, [user?.profile_picture]);

  // Birthday state
  const [birthdayMonth, setBirthdayMonth] = useState('');
  const [birthdayDay, setBirthdayDay] = useState('');
  const [savingBirthday, setSavingBirthday] = useState(false);
  const [birthdayMessage, setBirthdayMessage] = useState('');
  const [isEditingBirthday, setIsEditingBirthday] = useState(false);

  // Password change state
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

  const handleChangePassword = async () => {
    setPasswordMessage({ type: '', text: '' });
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Please fill in all fields' });
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'New password must be at least 6 characters' });
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    
    setSavingPassword(true);
    
    try {
      const token = localStorage.getItem('hyperdriveToken');
      const response = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordSection(false);
        setTimeout(() => setPasswordMessage({ type: '', text: '' }), 3000);
      } else {
        setPasswordMessage({ type: 'error', text: data.detail || 'Failed to change password' });
      }
    } catch (error) {
      setPasswordMessage({ type: 'error', text: 'Failed to change password. Please try again.' });
    } finally {
      setSavingPassword(false);
    }
  };

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ];

  const getDaysInMonth = (month) => {
    const daysMap = {
      '01': 31, '02': 29, '03': 31, '04': 30, '05': 31, '06': 30,
      '07': 31, '08': 31, '09': 30, '10': 31, '11': 30, '12': 31
    };
    return daysMap[month] || 31;
  };

  const formatBirthdayDisplay = (birthday) => {
    if (!birthday) return '';
    const [month, day] = birthday.split('-');
    const monthObj = months.find(m => m.value === month);
    return `${monthObj?.label || month} ${parseInt(day)}`;
  };

  // Initialize birthday from user data
  useEffect(() => {
    if (user?.birthday) {
      const [month, day] = user.birthday.split('-');
      setBirthdayMonth(month);
      setBirthdayDay(day);
      setIsEditingBirthday(false);
    } else {
      setIsEditingBirthday(true);
    }
  }, [user?.birthday]);

  const handleSaveBirthday = async () => {
    if (!birthdayMonth || !birthdayDay) return;
    
    setSavingBirthday(true);
    setBirthdayMessage('');
    
    try {
      const birthday = `${birthdayMonth}-${birthdayDay.padStart(2, '0')}`;
      
      // Use updateUser from AuthContext to save and update state
      const result = await updateUser({ birthday });
      
      if (result.success) {
        setBirthdayMessage('Birthday saved!');
        setIsEditingBirthday(false);
        setTimeout(() => setBirthdayMessage(''), 3000);
      } else {
        setBirthdayMessage('Failed to save');
      }
    } catch (error) {
      console.error('Save birthday error:', error);
      setBirthdayMessage('Failed to save');
    } finally {
      setSavingBirthday(false);
    }
  };

  return (
    <div className="page-content">
      <h1 className="page-title">Profile</h1>
      
      <div className="profile-layout-full">
        <div className="account-info-card">
          <h3 className="card-section-title">Account Information</h3>
          <div className="profile-avatar-section">
            <div className="profile-avatar-wrapper">
              <div className="profile-avatar-large">
                {user?.profile_picture ? (
                  <img 
                    key={imageKey}
                    src={uploadApi.getProfilePictureUrl(user.profile_picture, imageKey)} 
                    alt="Profile" 
                    className="profile-image"
                    onError={(e) => {
                      console.log('Profile image failed to load:', user.profile_picture);
                      e.target.style.display = 'none';
                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                    }}
                  />
                ) : null}
                <div className="profile-avatar-fallback" style={{ display: user?.profile_picture ? 'none' : 'flex' }}>
                  <User size={48} />
                </div>
              </div>
              <input 
                type="file" 
                ref={fileInputRef}
                onChange={handleProfilePictureUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: 'none' }}
              />
              <button 
                className="change-avatar-btn" 
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? <Loader2 size={14} className="spinner" /> : null}
                {isUploading ? 'Uploading...' : 'Change Avatar'}
              </button>
              {uploadProgress && !isUploading && (
                <span className="upload-status">{uploadProgress}</span>
              )}
            </div>
            <div className="profile-details">
              <div className="profile-field">
                <label>Name</label>
                <div className="input-with-button">
                  <input 
                    type="text" 
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your name"
                  />
                  <button className="save-button" onClick={handleSaveProfileName}>Save</button>
                </div>
              </div>
              <div className="profile-field">
                <label>Email</label>
                <p>{user?.email || 'hyperdrivetrading@gmail.com'}</p>
              </div>
              <div className="profile-field">
                <label>Role</label>
                <span className={user?.role === 'admin' ? 'admin-badge' : 'member-badge'}>
                  {user?.role === 'admin' ? 'Admin' : 'Member'}
                </span>
              </div>
              <div className="profile-field">
                <label>Member Since</label>
                <p>{user?.joinDate || 'December 31, 2025'}</p>
              </div>
              <div className="profile-field">
                <label>
                  <Cake size={14} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                  Birthday
                </label>
                {!isEditingBirthday && user?.birthday ? (
                  <div className="birthday-display-row">
                    <p className="birthday-value">{formatBirthdayDisplay(user.birthday)}</p>
                    <button 
                      className="edit-birthday-btn"
                      onClick={() => setIsEditingBirthday(true)}
                      data-testid="birthday-edit-btn"
                    >
                      <Pencil size={14} />
                      Edit
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="birthday-input-row-profile" data-testid="birthday-input-row">
                      <select
                        className="birthday-select-profile"
                        value={birthdayMonth}
                        onChange={(e) => {
                          setBirthdayMonth(e.target.value);
                          if (birthdayDay && parseInt(birthdayDay) > getDaysInMonth(e.target.value)) {
                            setBirthdayDay('');
                          }
                        }}
                        data-testid="birthday-month-select"
                      >
                        <option value="">Month</option>
                        {months.map(m => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      <select
                        className="birthday-select-profile"
                        value={birthdayDay}
                        onChange={(e) => setBirthdayDay(e.target.value)}
                        disabled={!birthdayMonth}
                        data-testid="birthday-day-select"
                      >
                        <option value="">Day</option>
                        {birthdayMonth && [...Array(getDaysInMonth(birthdayMonth))].map((_, i) => (
                          <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{i + 1}</option>
                        ))}
                      </select>
                      <button
                        className="save-button"
                        onClick={handleSaveBirthday}
                        disabled={savingBirthday || !birthdayMonth || !birthdayDay}
                        data-testid="birthday-save-btn"
                      >
                        {savingBirthday ? 'Saving...' : 'Save'}
                      </button>
                      {user?.birthday && (
                        <button
                          className="cancel-button"
                          onClick={() => {
                            const [month, day] = user.birthday.split('-');
                            setBirthdayMonth(month);
                            setBirthdayDay(day);
                            setIsEditingBirthday(false);
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    {birthdayMessage && <span className="birthday-save-message">{birthdayMessage}</span>}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="tradingview-integration-card">
          <h3 className="card-section-title">
            <Globe size={20} style={{ marginRight: '0.5rem' }} />
            TradingView Integration
          </h3>
          <div className="integration-content">
            <div className="form-group-profile">
              <label>TradingView Username</label>
              <p className="help-text">Enter your TradingView username to link your charting profile</p>
              <div className="input-with-button">
                <input 
                  type="text" 
                  placeholder="Your TradingView username"
                  value={tradingViewUsername}
                  onChange={(e) => setTradingViewUsername(e.target.value)}
                />
                <button className="save-button" onClick={handleSaveTradingViewUsername}>Save</button>
              </div>
            </div>
            <div className="integration-help">
              <p className="help-title">How to find your username</p>
              <ol className="help-steps">
                <li>Log in to TradingView.com</li>
                <li>Click your profile icon in the top left</li>
                <li>Your username is shown below your profile picture</li>
                <li>Or check your profile URL: tradingview.com/u/username</li>
              </ol>
              <p className="help-note">
                💡 Make sure to use the HyperDrive System Indicator on your TradingView charts for the best trading experience!
              </p>
            </div>
          </div>
        </div>

        {/* Security / Change Password Card */}
        <div className="security-card" style={{ 
          background: 'rgba(17, 24, 39, 0.8)',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 className="card-section-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Lock size={20} />
            Security
          </h3>
          
          {passwordMessage.text && (
            <div style={{
              padding: '0.75rem 1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              background: passwordMessage.type === 'success' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              border: `1px solid ${passwordMessage.type === 'success' ? 'rgba(16, 185, 129, 0.5)' : 'rgba(239, 68, 68, 0.5)'}`,
              color: passwordMessage.type === 'success' ? '#10b981' : '#ef4444'
            }}>
              {passwordMessage.text}
            </div>
          )}
          
          {!showPasswordSection ? (
            <button 
              onClick={() => setShowPasswordSection(true)}
              style={{
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
              data-testid="change-password-btn"
            >
              <Lock size={16} />
              Change Password
            </button>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '400px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                  Current Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(0,0,0,0.3)',
                      color: 'white',
                      fontSize: '0.95rem'
                    }}
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'pointer'
                    }}
                  >
                    {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      paddingRight: '2.5rem',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.2)',
                      background: 'rgba(0,0,0,0.3)',
                      color: 'white',
                      fontSize: '0.95rem'
                    }}
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      color: 'rgba(255,255,255,0.5)',
                      cursor: 'pointer'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                  Confirm New Password
                </label>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(0,0,0,0.3)',
                    color: 'white',
                    fontSize: '0.95rem'
                  }}
                  data-testid="confirm-password-input"
                />
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                <button
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: savingPassword ? 'not-allowed' : 'pointer',
                    fontWeight: '500',
                    opacity: savingPassword ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  data-testid="save-password-btn"
                >
                  {savingPassword ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  {savingPassword ? 'Saving...' : 'Update Password'}
                </button>
                <button
                  onClick={() => {
                    setShowPasswordSection(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordMessage({ type: '', text: '' });
                  }}
                  style={{
                    background: 'transparent',
                    color: 'rgba(255,255,255,0.7)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                  data-testid="cancel-password-btn"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Membership Status Card */}
        <div className="membership-status-card">
          <h3 className="card-section-title">
            <Award size={20} style={{ marginRight: '0.5rem' }} />
            Membership Status
          </h3>
          <div className="membership-content">
            <div className="current-membership">
              <div className="membership-badge-large">
                {user?.membership_name || 'Free'}
                {user?.membership_status === 'trialing' && (
                  <span className="trial-badge">7-Day Trial</span>
                )}
                {user?.membership_status === 'manual_bonus' && (
                  <span className="trial-badge bonus">30-Day Bonus</span>
                )}
              </div>
              {user?.membership_expires && (
                <p className="membership-expires">
                  {user.membership_status === 'trialing' ? 'Trial ends' : 
                   user.membership_status === 'manual_bonus' ? 'Bonus ends' : 'Access until'}: {formatDate(user.membership_expires)}
                </p>
              )}
            </div>
            
            {(user?.membership_name === 'Free' || !user?.membership_name || user?.membership_name === 'HyperDrive Indicator') && (
              <div className="upgrade-prompt">
                {(user?.membership_status === 'downgraded_payment_failed' || user?.membership_status === 'past_due') && (
                  <div className="payment-failed-banner" style={{
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                  }}>
                    <p style={{ margin: 0, color: '#fca5a5', fontWeight: 600 }}>
                      Your membership was paused because your last payment didn't go through.
                    </p>
                    <p style={{ margin: '6px 0 12px', color: '#e5e7eb', fontSize: '14px' }}>
                      Update your card below to keep your original pricing — Stripe will retry the failed payment automatically.
                    </p>
                    <button
                      className="manage-billing-btn"
                      onClick={handleOpenBillingPortal}
                      data-testid="manage-billing-btn-downgraded"
                    >
                      <CreditCard size={16} style={{ marginRight: '6px' }} />
                      Update Payment Method
                    </button>
                  </div>
                )}
                <p className="upgrade-text">
                  {user?.membership_name === 'HyperDrive Indicator' 
                    ? 'Want live trading access? Upgrade to unlock live sessions!' 
                    : 'Upgrade to unlock courses and live trading sessions!'}
                </p>
                <div className="upgrade-plans">
                  {memberships.filter(m => m.name !== 'Free' && m.visible !== false && m.name !== user?.membership_name).map(membership => (
                    <div key={membership.id} className={`upgrade-plan-option ${membership.category === 'indicator' ? 'indicator-option' : ''}`}>
                      <div className="plan-option-header">
                        <span className="plan-option-name">{membership.name}</span>
                        <span className="plan-option-price">
                          ${membership.price}{membership.period === 'month' ? '/mo' : ''}
                        </span>
                      </div>
                      {membership.category === 'indicator' && (
                        <span className="indicator-badge-small">INDICATOR ONLY</span>
                      )}
                      <p className="plan-option-desc">{membership.description}</p>
                      <button 
                        className={`upgrade-btn ${membership.category === 'indicator' ? 'indicator-btn' : ''}`}
                        onClick={() => openCheckoutModal(membership)}
                        disabled={upgradingMembershipId !== null}
                      >
                        {upgradingMembershipId === membership.id ? 'Processing...' : (membership.category === 'indicator' ? 'Get Indicator' : 'Get Started')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {user?.membership_name && user?.membership_name !== 'Free' && (
              <>
                <div className="membership-features">
                  <p className="features-title">Your benefits:</p>
                  <ul className="features-list">
                    {memberships.find(m => m.name === user.membership_name)?.features?.map((feature, idx) => (
                      <li key={idx}>
                        <CheckCircle size={14} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="manage-billing-section">
                  <button 
                    className="manage-billing-btn"
                    onClick={handleOpenBillingPortal}
                  >
                    <CreditCard size={16} />
                    Manage Billing
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={!!checkoutModalMembership}
        onClose={closeCheckoutModal}
        membership={checkoutModalMembership}
        userId={user?.id}
        onCheckout={handleCheckoutWithCoupon}
      />
    </div>
  );
};

export default ProfilePage;